import React, { useRef, useState, useEffect } from "react";
import styles from "styles.module.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addMonths, addWeeks } from "date-fns";
import { useTranslation } from "react-i18next";
import Filter from "./Filter";
import {
	getPurchaseStaticsApi,
	PurchaseStatisticFilter,
} from "api/purchaseStatistic";
//@ts-ignore
import "./style.css";
import { PurchaseStatisticColumnItem } from "./utils/GridColumns";
import { useUserStore } from "store";
import { UserRole } from "interface/user";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { PurchaseStatistic } from "interface/purchaseStatistic";
import PurchaseStatisticMobileList from "./PurchaseStatisticMobileList";

const pageSize = 60;

const PurchaseStatisticPage = () => {
	const { t } = useTranslation();
	const { roles } = useUserStore();
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [mobileData, setMobileData] = useState<PurchaseStatistic[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	var fromDate = addMonths(new Date(), -1);

	if (roles.includes(UserRole.Admin)) {
		fromDate = addWeeks(new Date(), -1);
	}

	const [filterOptions, setFilterOptions] = useState<PurchaseStatisticFilter>({
		take: pageSize,
		skip: 0,
		fromDate: fromDate,
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	useEffect(() => {
		const updateMobileView = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		updateMobileView();
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		setMobileLoading(true);

		getPurchaseStaticsApi({
			...filterOptions,
			skip: 0,
			take: pageSize,
		})
			.then((response) => {
				setMobileData((response || []) as PurchaseStatistic[]);
			})
			.catch((error) => {
				console.log(error);
				setMobileData([]);
			})
			.finally(() => {
				setMobileLoading(false);
			});
	}, [filterOptions, isMobileView]);

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("PurchaseStatistic")}
				</legend>
			</div>

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }}>
				{isMobileView ? (
					<PurchaseStatisticMobileList
						items={mobileData}
						loading={mobileLoading}
					/>
				) : (
					<GridWithGroupPaging
						dataKey=""
						gridColumns={PurchaseStatisticColumnItem()}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getPurchaseStaticsApi}
						pageable={false}
						showSelectedFilters={false}
						className={"purchaseStatistic-group-grid"}
						groupable={{ footer: "visible", enabled: false }}
						excelProps={{
							filterable: true,
							fileName: `purchase_statistic_${new Date().toISOString()}.xlsx`,
						}}
						group={[
							{
								field: "group1",
								aggregates: [{ field: "netLiveWeightKg", aggregate: "sum" }],
							},
							{
								field: "group2",
								aggregates: [{ field: "netLiveWeightKg", aggregate: "sum" }],
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default PurchaseStatisticPage;
